/**
 * Who may see a task — plan §8.1.1, acceptance criterion 26.
 *
 * The SDR queue implements this in SQL for speed; this module states the rule
 * in one place so it can be tested, and so the UI and the API cannot quietly
 * disagree about it.
 *
 * **Why it exists.** The queue joined `mailboxes` with an INNER join, so a task
 * with no mailbox was dropped from every queue — silently, and for an entire
 * category of work. Research tasks send nothing and therefore have no mailbox,
 * which meant §17.5's "a failed verification produces a research task naming
 * the contact" produced a task nobody could ever see: the contact stayed
 * blocked from enrollment and the instructions for unblocking them were
 * invisible. §6.3's research tasks for unactionable accounts shared the fate.
 *
 * A sending task routes by MAILBOX — it belongs to whoever owns the identity it
 * leaves from. A task with no mailbox has no such route, so it follows the
 * ACCOUNT instead. Neither may cross §8.1.1's exclusivity line.
 */

export interface TaskVisibilityInput {
  /** `mailboxes.user_id` for the task's mailbox; null when it has none. */
  mailboxOwnerId: number | null;
  /** The account's owner, or null when unassigned. */
  accountAssignedTo: number | null;
  viewerUserId: number;
  /** Managers and admins see the whole environment (§16.0). */
  viewerSeesWholeTeam: boolean;
}

export function taskVisibleToRep(input: TaskVisibilityInput): boolean {
  if (input.viewerSeesWholeTeam) return true;

  /**
   * Exclusivity first, and it beats everything below.
   *
   * Criterion 26 is about TASKS: an SDR must neither view nor act on tasks
   * belonging to another rep's account. Scoping by mailbox alone would leak a
   * task the moment an account changed hands, because the pin does not move
   * with the account (§5.5.3).
   */
  if (input.accountAssignedTo !== null && input.accountAssignedTo !== input.viewerUserId) {
    return false;
  }

  // A sending task belongs to whoever owns the mailbox it leaves from.
  if (input.mailboxOwnerId !== null) return input.mailboxOwnerId === input.viewerUserId;

  /**
   * No mailbox, so no route — it follows the account.
   *
   * An UNASSIGNED account yields nothing here, which is deliberate: §8.1.1's
   * "unowned accounts generate no tasks ... never an item in an SDR's queue".
   * Showing it to every rep instead would be worse than hiding it, and the
   * manager/admin unrouted view is where unowned work belongs.
   */
  return input.accountAssignedTo === input.viewerUserId;
}

/**
 * What `tasks.kind` may hold — E1.14, plans/execution-plan-2026-09.md §2.2 0.1.
 *
 * Two lists, because they differ. `SEND_KINDS` is what a sequence template may
 * be and what the gates, compose and the §7.2 consent matrix distinguish;
 * `TASK_KINDS` adds `job_change`, a research task with its own panel that is
 * never a send. The column is free text in Postgres — no CHECK, because
 * `job_change` was added without one and a constraint needs a data check
 * first — so this module is the only thing between a request body and the
 * column.
 *
 * **Why a 422 and not a silent default.** `POST /sequences` inserted
 * `body.kind ?? 'sdr'` unvalidated. A value like `Marketing` collapsed to
 * `sdr` for the gates and for compose (`String(kind) === 'marketing' ?
 * 'marketing' : 'sdr'`), yet was in NEITHER list the consent stops compute
 * from — so its tasks survived a reply-stop and a manual withdrawal of
 * consent, and the unsubscribe-link path *paused* them as if they were SDR,
 * the wrong outcome for bulk mail. And a template posted as
 * `kind: 'marketing'` enrolled contacts into marketing-kind tasks that the
 * SDR writer then generated prose for — bypassing §6.2's "the AI selects,
 * never writes" entirely.
 */
export const SEND_KINDS = ['sdr', 'marketing'] as const;
/** Which kind of mail is being considered. Mirrors `tasks.kind`. */
export type SendKind = (typeof SEND_KINDS)[number];

export const TASK_KINDS = [...SEND_KINDS, 'job_change'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export function isSendKind(value: unknown): value is SendKind {
  return typeof value === 'string' && (SEND_KINDS as readonly string[]).includes(value);
}

export function isTaskKind(value: unknown): value is TaskKind {
  return typeof value === 'string' && (TASK_KINDS as readonly string[]).includes(value);
}

/**
 * The assertion every `tasks` insert makes where the kind is not a literal —
 * a template's `kind` read back from the database, a bulk job's stored
 * params. Throws rather than defaulting: a kind this module has never heard
 * of reaching a task row is corruption, not a preference, and the literal
 * sites are held to the same list by the column's type in @actos/db.
 */
export function sendKind(value: unknown): SendKind {
  if (isSendKind(value)) return value;
  throw new TypeError(
    `Not a send kind: ${JSON.stringify(value)} (expected one of ${SEND_KINDS.join(', ')})`,
  );
}

export function taskKind(value: unknown): TaskKind {
  if (isTaskKind(value)) return value;
  throw new TypeError(
    `Not a task kind: ${JSON.stringify(value)} (expected one of ${TASK_KINDS.join(', ')})`,
  );
}
